import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { boardService, UpdateBoardRequest } from "../../../lib/api/boards";
import { useBoardStore } from "../../../stores/boardStore";
import { usePlanFeaturesStore } from "../../../stores/planFeaturesStore";

interface UseDateRangeProps {
  boardId: string | undefined;
}

export const useDateRange = ({ boardId }: UseDateRangeProps) => {
  const queryClient = useQueryClient();
  const setDateRange = useBoardStore(state => state.setDateRange);
  const getFeatureValue = usePlanFeaturesStore(state => state.getFeatureValue);

  // Upgrade modal state exposed to the parent component
  const [dateRangeUpgrade, setDateRangeUpgrade] = useState<{
    isOpen: boolean;
    currentDays: number;
  }>({ isOpen: false, currentDays: 0 });

  // Update date range mutation
  const updateDateRangeMutation = useMutation({
    mutationFn: ({ startDate, endDate }: { startDate: Date | null; endDate: Date | null }) => {
      if (!boardId) throw new Error('Board ID is required');

      const updateData: UpdateBoardRequest = {};

      // Format dates as YYYY-MM-DD to avoid timezone issues
      if (startDate) {
        const year = startDate.getFullYear();
        const month = String(startDate.getMonth() + 1).padStart(2, '0');
        const day = String(startDate.getDate()).padStart(2, '0');
        updateData.startDate = `${year}-${month}-${day}`;
      }

      if (endDate) {
        const year = endDate.getFullYear();
        const month = String(endDate.getMonth() + 1).padStart(2, '0');
        const day = String(endDate.getDate()).padStart(2, '0');
        updateData.endDate = `${year}-${month}-${day}`;
      }

      return boardService.updateBoard(boardId, updateData);
    },
    onMutate: () => {
      useBoardStore.getState().setIsDataLoading(true);
    },
    onSuccess: () => {
      // Invalidate the unified board payload query
      queryClient.invalidateQueries({ queryKey: ["board-payload", boardId] });
    },
    onError: (error) => {
      console.error('Error updating date range:', error);
      // Clear loading on error since no refetch will follow
      useBoardStore.getState().setIsDataLoading(false);
    }
    // Note: do NOT clear isDataLoading in onSettled — the board-payload refetch
    // triggered by onSuccess.invalidateQueries is still in progress. useBoardFullData
    // clears isDataLoading when the refetch completes.
  });

  const handleDateRangeChange = (newRange: { startDate: Date | null; endDate: Date | null }) => {
    // Get current date range from store for comparison
    const currentRange = useBoardStore.getState().dateRange;

    // Check if dates actually changed
    const startDateChanged = newRange.startDate?.getTime() !== currentRange.startDate?.getTime();
    const endDateChanged = newRange.endDate?.getTime() !== currentRange.endDate?.getTime();

    // If nothing changed, don't update
    if (!startDateChanged && !endDateChanged) {
      console.log('[useDateRange] No date changes detected, skipping update');
      return;
    }

    // ─── Plan Feature Gate: max_time_range_days ─────────────────────
    if (newRange.startDate && newRange.endDate) {
      const maxDays = getFeatureValue('max_time_range_days');
      if (typeof maxDays === 'number' && maxDays !== -1) {
        const diffMs = newRange.endDate.getTime() - newRange.startDate.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays > maxDays) {
          // Show upgrade modal via exposed state — do NOT update date range
          setDateRangeUpgrade({ isOpen: true, currentDays: diffDays });
          return;
        }
      }
    }
    // ────────────────────────────────────────────────────────────────

    // Update store immediately for responsive UI
    setDateRange(newRange);

    // Send mutation to update server if we have valid dates
    if (boardId && newRange.startDate && newRange.endDate) {
      updateDateRangeMutation.mutate({
        startDate: newRange.startDate,
        endDate: newRange.endDate,
      });
    }
  };

  return {
    handleDateRangeChange,
    isUpdating: updateDateRangeMutation.isPending,
    // Date range upgrade modal state
    dateRangeUpgrade,
    closeDateRangeUpgrade: () => setDateRangeUpgrade({ isOpen: false, currentDays: 0 }),
  };
};
